import type { PurchaseOrder } from "@/redistribution/models/PurchaseOrder.js";
import type { Sku } from "@/redistribution/models/Sku.js";
import moment from "moment";

export type SkuForecastsResult = Record<string, number>;

interface AvgDaysOfStockSku {
  pmKey: string;
  skuNumber: string;
  dailyRateOfSale: number;
}

interface AvgDaysOfStockRequestBody {
  skus: AvgDaysOfStockSku[];
  fallbackAverageDaysOfStockBySku: SkuForecastsResult;
  arrivalDate: string;
  rateOfSaleDays: number;
  avgWOSWarehouses: string;
}

export const skuForecastCache = new Map<
  string,
  { expiresAt: number; value: SkuForecastsResult }
>();
export const skuForecastInFlight = new Map<
  string,
  Promise<SkuForecastsResult>
>();

/**
 * Get the daily rate of sale for a SKU based on the configured rate-of-sale days
 */
function getDailyRateForRateOfSale(sku: Sku, rateOfSaleDays: number): number {
  const rosKey = `roS${String(rateOfSaleDays)}` as keyof Sku;
  const rate = Number(sku[rosKey] ?? sku.dailyRateOfSale);
  return Number.isFinite(rate) ? rate : 0;
}

/**
 * Build the request body for average days of stock API call
 */
export function buildAvgDaysOfStockRequestBody(
  avgWOSWarehouses: string[],
  purchaseOrders: PurchaseOrder[],
  rateOfSaleDays: number,
  arrivalDate: string
): AvgDaysOfStockRequestBody {
  const uniqueSkusByNumber = new Map<string, Sku>();

  for (const po of purchaseOrders) {
    for (const sku of po.skus) {
      if (!sku.skuNumber || uniqueSkusByNumber.has(sku.skuNumber)) {
        continue;
      }
      uniqueSkusByNumber.set(sku.skuNumber, sku);
    }
  }

  const skus: AvgDaysOfStockSku[] = [];
  const fallbackAverageDaysOfStockBySku: SkuForecastsResult = {};
  const seenPmKeys = new Set<string>();

  for (const sku of uniqueSkusByNumber.values()) {
    const dailyRateOfSale = getDailyRateForRateOfSale(sku, rateOfSaleDays);
    if (dailyRateOfSale <= 0 || !sku.skuNumber) {
      continue;
    }

    fallbackAverageDaysOfStockBySku[sku.skuNumber] =
      sku.lineItemQuantity / dailyRateOfSale;

    if (!sku.pmKey || seenPmKeys.has(sku.pmKey)) {
      continue;
    }

    seenPmKeys.add(sku.pmKey);
    skus.push({
      pmKey: sku.pmKey,
      skuNumber: sku.skuNumber,
      dailyRateOfSale,
    });
  }

  return {
    skus,
    fallbackAverageDaysOfStockBySku,
    arrivalDate,
    rateOfSaleDays,
    avgWOSWarehouses: avgWOSWarehouses.join(","),
  };
}

/**
 * Get the earliest arrival date from a set of purchase orders
 */
export function getEarliestArrivalDateString(
  purchaseOrders: PurchaseOrder[]
): string {
  let earliestArrivalDate: Date | null = null;
  for (const po of purchaseOrders) {
    const arrivalDate = new Date(po.arrivalDate);
    if (!earliestArrivalDate || arrivalDate < earliestArrivalDate) {
      earliestArrivalDate = arrivalDate;
    }
  }
  const arrivalDateMoment = earliestArrivalDate
    ? moment(earliestArrivalDate).add(-1, "days")
    : moment();
  const arrivalDateStrParts = arrivalDateMoment
    .startOf("day")
    .toISOString()
    .split("T");
  return arrivalDateStrParts[0] ?? arrivalDateMoment.toISOString();
}
